import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import type { MatterAssignee } from "@/lib/types";
import type { User } from "@/lib/types/Firm";
import { formatUsersName } from "@/lib/utils/format";
import {
  getUserAccountStatus,
  type UserAccountStatus,
} from "@/lib/utils/user-account-status";
import { Badge } from "@/components/ui";
import { Avatar } from "@/components/ui/shadcn/Avatar/Avatar";
import {
  UserProfilePhoto,
  UserProfilePhotoFallback,
} from "../UserProfilePhoto/UserProfilePhoto";

type PhotoUser = Pick<
  User,
  "id" | "firstName" | "lastName" | "profilePicturePresignedUrlKey"
> & {
  roles?: string[];
  status?: string | null;
  enabled?: boolean | null;
};

export const UserPhotoRow = ({
  users = [],
  showIconsAmount = 3,
  showTooltips = false,
  assignees,
  statusFor,
}: {
  users?: User[];
  showIconsAmount?: number;
  showTooltips?: boolean;
  assignees?: MatterAssignee[];
  statusFor?: (userId: string) => UserAccountStatus | undefined;
}) => {
  const usersToUse: PhotoUser[] = assignees
    ? [
        ...new Map(
          assignees.map((assignee) => [assignee.user.id, assignee.user]),
        ).values(),
      ].map((user) => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        profilePicturePresignedUrlKey: user.profilePicturePresignedUrlKey,
        status: user.status,
        enabled: user.enabled,
        roles: [
          ...new Set(
            assignees
              .filter((assignee) => assignee.user.id === user.id)
              .map((assignee) => assignee.role.label),
          ),
        ],
      }))
    : users;
  const visibleUsers = usersToUse.slice(0, showIconsAmount);
  const remainingUsers = usersToUse.slice(showIconsAmount);
  const userDetails = (user: PhotoUser) => (
    <div className="min-w-0 py-1.5 leading-5">
      <div className="flex items-center gap-2 font-medium">
        <span className="min-w-0 break-words">{formatUsersName(user)}</span>
        {(statusFor?.(user.id) ??
          getUserAccountStatus(user.status, user.enabled)) && (
          <Badge variant="muted" className="shrink-0">
            {statusFor?.(user.id) ??
              getUserAccountStatus(user.status, user.enabled)}
          </Badge>
        )}
      </div>
      {user.roles?.length ? (
        <div className="text-muted-foreground break-words">
          {user.roles.join(", ")}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="flex -space-x-1.5">
      {visibleUsers.map((user) => {
        const avatar = (
          <UserProfilePhoto
            className="border-background h-7 w-7 border-2"
            initialsClassName="text-[10px] font-medium"
            user={user}
          />
        );

        return showTooltips ? (
          <TooltipWrapper
            key={user.id}
            message={userDetails(user)}
            contentClassName="border border-border bg-popover text-popover-foreground shadow-md"
          >
            <div className="relative">{avatar}</div>
          </TooltipWrapper>
        ) : (
          <div key={user.id} className="relative">
            {avatar}
          </div>
        );
      })}
      {remainingUsers.length > 0 && (
        <TooltipWrapper
          contentClassName="border border-border bg-popover text-popover-foreground shadow-md"
          message={
            <div className="max-h-64 w-72 max-w-[calc(100vw-2rem)] divide-y overflow-y-auto">
              {remainingUsers.map((user) => (
                <div key={user.id}>{userDetails(user)}</div>
              ))}
            </div>
          }
        >
          <div className="relative">
            <Avatar className="text-dark-primary border-background h-7 w-7 border-2">
              <UserProfilePhotoFallback
                text={`+${remainingUsers.length}`}
                className="text-[10px] font-medium"
              />
            </Avatar>
          </div>
        </TooltipWrapper>
      )}
    </div>
  );
};
